import { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { LocalDate } from "@js-joda/core";
import { CheckCreateReq, ImportPaymentReq } from "./Data/ApiTypes";
import { CheckCreateReqApi, ImportPaymentReqApi } from "./Data/ApiTransport";
import { showBrowserDate } from "./Data/Date";
import { HeaderText } from "./Controls/HeaderText";
import { MoneyInput } from "./Controls/MoneyInput";
import { parseCheckCreateReq, parseImportPaymentReq } from "./Data/ApiParse";

type PaymentKind = "Check" | "Cash" | "Other";

type RecordPaymentResult =
    | { kind: "check";  data: CheckCreateReq   }
    | { kind: "import"; data: ImportPaymentReq  };

interface CheckFormFields extends CheckCreateReqApi {
    paymentKind: PaymentKind;
}

interface ImportFormFields extends ImportPaymentReqApi {
    paymentKind: PaymentKind;
    paymentDate: string;
}

type FormFields = CheckFormFields | ImportFormFields;

interface RecordPaymentFormModalProps {
    facilityId: number;
    parkerId:   number;
    show:       boolean;
    setShow:    ( show: boolean ) => void;
    onSave:     ( result: RecordPaymentResult ) => void;
}

export function RecordPaymentFormModal( { facilityId, parkerId, show, setShow, onSave }: RecordPaymentFormModalProps ) {
    const [paymentKind, setPaymentKind] = useState<PaymentKind>( "Check" );
    const today = showBrowserDate( LocalDate.now() );

    const checkForm = useForm<CheckFormFields>({
        defaultValues: {
            paymentKind: "Check",
            checkDate:   showBrowserDate( LocalDate.now() ),
            checkMemo:   "",
            checkNumber: "",
            amount:       0,
        },
        mode: "onSubmit"
    });

    const importForm = useForm<ImportFormFields>({
        defaultValues: {
            paymentKind: "Cash",
            note:        "",
            amount:      0,
            paymentDate: showBrowserDate( LocalDate.now() ),
        }
    });

    useEffect(() => {
        if ( show ) {
            setPaymentKind( "Check" );
            checkForm.reset({
                paymentKind: "Check",
                checkDate:   showBrowserDate( LocalDate.now() ),
                checkMemo:   "",
                checkNumber: "",
                amount:       0,
            });
            importForm.reset({
                paymentKind: "Cash",
                note:        "",
                amount:      0,
                paymentDate: showBrowserDate( LocalDate.now() ),
            });
        }
    }, [show]);

    function submitCheck( data: CheckFormFields ) {
        onSave({ kind: "check", data: parseCheckCreateReq( data ) });
        setShow( false );
    }

    function submitImport( data: ImportFormFields ) {
        onSave({ kind: "import", data: parseImportPaymentReq({ note: data.note, amount: data.amount, paymentDate: data.paymentDate, type: paymentKind }) });
        setShow( false );
    }

    const noteWatch = importForm.watch( "note" );

    return <Modal show={show} centered onEscapeKeyDown={() => setShow( false )}>
        <Modal.Header className="fs-5">
            <HeaderText>Record Payment</HeaderText>
        </Modal.Header>

        <Modal.Body>
            <Form.Group className="mb-3">
                <Form.Label>Payment Type</Form.Label>
                <Form.Select
                    value={paymentKind}
                    onChange={e => setPaymentKind( e.target.value as PaymentKind )}>
                    <option value="Check">Check</option>
                    <option value="Cash">Cash</option>
                    <option value="Other">Other</option>
                </Form.Select>
            </Form.Group>

            {paymentKind === "Check" && (
                <Form onSubmit={checkForm.handleSubmit( submitCheck )} id="record-payment-form">
                    <Input
                        type="date"
                        label="Check Date"
                        explanation="Date written on the check"
                        max={today}
                        {...checkForm.register( "checkDate", {
                            required: "Check date is required",
                            validate: v => v <= today || "Check date cannot be in the future"
                        })}
                        error={checkForm.formState.errors.checkDate} />

                    <MoneyInput
                        label="Amount"
                        explanation="Check amount in dollars and cents"
                        min="0"
                        {...checkForm.register( "amount", {
                            valueAsNumber: true,
                            required: "Amount is required",
                            min: {
                                value:   0.01,
                                message: "Amount must be greater than $0.00"
                            },
                            validate: (value) =>
                                !isNaN( value ) && value > 0 || "Please enter a valid amount greater than $0.00"
                        })}
                        error={checkForm.formState.errors.amount} />

                    <Input
                        type="text"
                        label="Check Number"
                        maxLength={16}
                        {...checkForm.register( "checkNumber", {
                            required:  "Check number is required",
                            maxLength: { value: 16, message: "Check number must be 16 characters or less" }
                        })}
                        error={checkForm.formState.errors.checkNumber} />

                    <Input
                        type="text"
                        label="Check Memo"
                        maxLength={24}
                        {...checkForm.register( "checkMemo", {
                            required:  "Check memo is required",
                            maxLength: { value: 24, message: "Check memo must be 24 characters or less" }
                        })}
                        error={checkForm.formState.errors.checkMemo} />
                </Form>
            )}

            {( paymentKind === "Cash" || paymentKind === "Other" ) && (
                <Form onSubmit={importForm.handleSubmit( submitImport )} id="record-payment-form">
                    <Input
                        type="date"
                        label="Payment Date"
                        explanation="Date the payment was received"
                        max={today}
                        {...importForm.register( "paymentDate", {
                            required: "Payment date is required",
                            validate: v => v <= today || "Payment date cannot be in the future"
                        })}
                        error={importForm.formState.errors.paymentDate} />

                    <Input
                        type="text"
                        label="Note"
                        maxLength={64}
                        placeholder={paymentKind === "Cash" ? "Ex: Cash received at facility" : "Ex: Authorize.Net Payment"}
                        explanation={`Where was the payment made? ${noteWatch.length}/64`}
                        {...importForm.register( "note", {
                            required: "Note is required"
                        })}
                        error={importForm.formState.errors.note} />

                    <MoneyInput
                        label="Amount"
                        inputClassName="text-end"
                        explanation="Amount in dollars and cents"
                        min="0"
                        {...importForm.register( "amount", {
                            valueAsNumber: true,
                            required:      "Amount is required",
                            validate: (value: number) => value != 0 && !isNaN( value ) || "Please enter a valid number"
                        })}
                        error={importForm.formState.errors.amount} />
                </Form>
            )}
        </Modal.Body>

        <Modal.Footer className="d-flex">
            <Button className="flex-grow-1" onClick={() => setShow( false )}>Cancel</Button>
            <Button className="flex-grow-1 btn-main" type="submit" form="record-payment-form">Save</Button>
        </Modal.Footer>
    </Modal>;
}
